import { useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IChangeDashboardUserPasswordModalProps } from '@/features/dashboard-users/components/changePassword/ChangeDashboardUserPasswordModal';
import { IDashboardUserActionModalProps } from '@/features/dashboard-users/components/DashboardUserModal.types';
import { IConfirmationReset2faModalProps } from '@/features/dashboard-users/components/manage/ConfirmationReset2faModal';
import { ISAMLKeyModalProps } from '@/features/dashboard-users/components/manage/SAMLKeyModal';
import { ISupportUserConfirmModalProps } from '@/features/dashboard-users/components/SupportUserConfirmModal';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useDashboardUserInformation } from '@/features/dashboard-users/hooks/useDashboardUserInformation';
import { ISupportUser, ssoStatusOptions, TSsoStatus } from '@/features/dashboard-users/store/support-user.model';
import { getSupportUserScopes } from '@/features/roles/helpers/getSupportUserScopes';
import useAdvancedRole, { useAllAdvancedRoles } from '@/features/roles/hooks/useAdvancedRole';
import { advancedRoleStatus, TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { EScope } from '@/features/roles/store/models/role.model';

import { openModal } from '@/store/actions/core.actions';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { selectDashboardUser } from '@/store/selectors';

export default function useManageDashboardUser(dashboardUserId: string, onCloseManageDashboardUser) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { site, api } = useDashboardConfig();
  const isSSO = useMemo(() => api.sso?.responseType === 'token', [api.sso?.responseType]);

  const { id: currentDashboardUserId } = useCurrentSupportUser();
  const { state: dashboardUser, isLoading: isDashboardUserLoading } = useAppSelector((state) =>
    selectDashboardUser(dashboardUserId)(state),
  );
  const { isBlocked, lastLogin, creationDate, accessExpirationDate } = useDashboardUserInformation(dashboardUser);
  const hasSamlKey = useMemo(() => !!dashboardUser.samlKey?.trim(), [dashboardUser.samlKey]);
  const isOwnAccount = useMemo(
    () => currentDashboardUserId === dashboardUser.id,
    [currentDashboardUserId, dashboardUser.id],
  );

  const scopes = getSupportUserScopes(dashboardUser);
  const uniqueScopes: EScope[] = useMemo(() => [...new Set(scopes)] as EScope[], [scopes]);
  const role = useAdvancedRole();
  const {
    role: advancedRole,
    advandeRoleFromScopes,
    advandeRoleFromSlug,
    config: advancesRolesConfig,
  } = useAccesControl();
  const { isLoading: isAdvancedRolesLoading } = useAllAdvancedRoles();

  const dashboardUserRole = useMemo(
    () =>
      dashboardUser.metadata?.role?.status === advancedRoleStatus.VALIDATED
        ? advandeRoleFromSlug(dashboardUser.metadata.role.slug)
        : advandeRoleFromScopes(uniqueScopes),
    [
      advandeRoleFromScopes,
      advandeRoleFromSlug,
      dashboardUser.metadata?.role?.slug,
      dashboardUser.metadata?.role?.status,
      uniqueScopes,
    ],
  );

  const selectSavedValue = useRef<ISupportUser | undefined>(undefined);

  const ssoStatusInfo: {
    status: TSsoStatus;
    color: string;
    tooltip?: string;
  } = useMemo(() => {
    if (isSSO) {
      return hasSamlKey
        ? {
            status: ssoStatusOptions.active,
            color: 'text-success',
          }
        : {
            status: ssoStatusOptions.SAMLKeyMissing,
            color: 'text-warning',
            tooltip: t(
              `pages.administration.supportsUsers.details.account.sso.tooltip.${ssoStatusOptions.SAMLKeyMissing}`,
            ),
          };
    } else {
      return {
        status: ssoStatusOptions.inactive,
        color: 'text-error',
        tooltip: t(`pages.administration.supportsUsers.details.account.sso.tooltip.${ssoStatusOptions.inactive}`),
      };
    }
  }, [hasSamlKey, isSSO, t]);

  const onAdvancedRoleChange = useCallback(
    (newRoleSlug: TAdvancedRoleSlug, user: ISupportUser) => {
      const newRole = advancesRolesConfig.find((role) => newRoleSlug === role.slug);
      const scopes = getSupportUserScopes(user);
      const oldRole = user.metadata?.role?.slug
        ? advancesRolesConfig.find((role) => user.metadata.role.slug === role.slug)
        : advandeRoleFromScopes(scopes);
      if (newRole.slug !== oldRole.slug) {
        selectSavedValue.current = user;
        dispatch(
          openModal<ISupportUserConfirmModalProps>('supportUserConfirmModal', {
            user,
            oldRole,
            newRole,
          }),
        );
      }
    },
    [dispatch, advancesRolesConfig, advandeRoleFromScopes],
  );

  const onClickUpdateSamlKey = useCallback(() => {
    dispatch(
      openModal<ISAMLKeyModalProps>('samlKeyModal', {
        samlKey: dashboardUser.samlKey,
        id: dashboardUser.id,
        username: dashboardUser.username,
      }),
    );
  }, [dashboardUser.id, dashboardUser.samlKey, dashboardUser.username, dispatch]);

  const onReset2Fa = useCallback(() => {
    dispatch(
      openModal<IConfirmationReset2faModalProps>('confirmationReset2faModal', {
        dashboardUser,
      }),
    );
  }, [dashboardUser, dispatch]);

  const toggleUpdatePasswordrModal = useCallback(
    () =>
      dispatch(
        openModal<IChangeDashboardUserPasswordModalProps>('changeDashboardUserPasswordModal', {
          dashboardUser,
        }),
      ),
    [dashboardUser, dispatch],
  );

  const toggleSuspendUserModal = () => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('suspendDashboardUserModal', {
        supportUser: dashboardUser,
        onFinishAction: onCloseManageDashboardUser,
      }),
    );
  };

  const toggleRemoveUserModal = useCallback(() => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('dashboardUserRemoveModal', {
        supportUser: dashboardUser,
        onFinishAction: onCloseManageDashboardUser,
      }),
    );
  }, [dashboardUser, dispatch, onCloseManageDashboardUser]);

  return {
    t,
    dashboardUser,
    organisationName: site.name,
    isSSO,
    isDashboardUserLoading,
    isBlocked,
    lastLogin,
    creationDate,
    isOwnAccount,
    role,
    advancedRole,
    isAdvancedRolesLoading,
    dashboardUserRole,
    ssoStatusInfo,
    advancesRolesConfig,
    accessExpirationDate,
    hasSamlKey,
    onAdvancedRoleChange,
    onClickUpdateSamlKey,
    onReset2Fa,
    toggleUpdatePasswordrModal,
    toggleRemoveUserModal,
    toggleSuspendUserModal,
  };
}
